import React, { useEffect, useRef, useState } from "react";
import styles from "styles.module.css";
import { useTranslation } from "react-i18next";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addDays, addMonths } from "date-fns";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import {
	getRegistrationOverviewApi,
	RegistrationOverviewParams,
} from "api/registration";
import { registrationOverviewGridColumns } from "./utils/OverviewGridColumns";
import OverviewFilter from "./utils/OverviewFilter";
import { useLocation, useNavigate } from "react-router";
import { useMenuItemStore } from "store/menuItem";
import { findMenuItemByPath } from "utils/helper";
import { getPageAccessApi } from "api/menu";
import { PageAccessByMenu } from "interface/user";
import { PageTypePath } from "utils/pages";
import CustomButton from "ui/Button";

const RegistrationOverviewPage: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { menuItems } = useMenuItemStore();

	const currentPath = location.pathname;

	const [pageAccess, setPageAccess] = useState<PageAccessByMenu | null>(null);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const [filterOptions, setFilterOptions] =
		useState<RegistrationOverviewParams>({
			registrationTimeFrom: addDays(new Date(), -2),
			registrationTimeTo: new Date(),
			landingTimeFrom: addDays(new Date(), -2),
			landingTimeTo: new Date(),
		});

	useEffect(() => {
		const pageHandler = async () => {
			const menu = findMenuItemByPath(menuItems, currentPath);
			try {
				if (!pageAccess && menu) {
					const access = await getPageAccessApi(menu.id);
					setPageAccess(access);
				}
			} catch (error) {
				console.error("Error fetching page access:", error);
			}
		};
		pageHandler();
	}, [currentPath, menuItems, pageAccess]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("RegistrationOverview")}
				</legend>
			</div>
			<div>
				<OverviewFilter
					filterOptions={filterOptions}
					setFilterOptions={setFilterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				<GridWithGroupPaging
					// dataKey="registrationId"
					gridColumns={registrationOverviewGridColumns({})}
					exportGridAsPDFRef={pdfRef}
					exportGridAsExcelRef={excelRef}
					apiMethod={(params) =>
						getRegistrationOverviewApi(params).then((res) => res.data ?? [])
					}
					filter={filterOptions}
					itemAboveHeight={100}
					pageable={false}
					// hideVirtualScroll={true}
					lastColBtns={{
						width: 100,
						cells: {
							data: (props) => {
								return (
									<td
										style={{
											display: "flex",
											gap: 15,
											// justifyContent: "flex-end",
											paddingRight: 10,
										}}
									>
										{props.dataItem.numberOfLandingNotes > 0 && (
											<button
												className="k-button k-button-sm k-button-solid k-button-solid-base"
												style={{ minWidth: 28, color: "black" }}
												title={t("LandingNote")}
												onClick={() =>
													navigate(
														`/${PageTypePath.FisheryDepartmentLandingNotes}/${props.dataItem.registrationId}`,
													)
												}
											>
												L
											</button>
										)}
										{props.dataItem.numberOfSalesNotes > 0 && (
											<button
												className="k-button k-button-sm k-button-solid k-button-solid-base"
												// style={{ color: "black", width: 28 }}
												title={t("SalesNote")}
												onClick={() => {
													navigate(
														`/${PageTypePath.FisheryDepartmentSalesNotes}/${props.dataItem.registrationId}`,
													);
												}}
											>
												S
											</button>
										)}
									</td>
								);
							},
						},
					}}
				/>
			</div>
		</div>
	);
};

export default RegistrationOverviewPage;
